import * as React from 'react';
import axios, { AxiosError } from 'axios';
import {
  Banner,
  DefaultButton,
  DialogActions,
  DialogContent,
  PrimaryButton,
  TextField,
  Typography,
  makeStyles,
} from '@bb-ui/react-library';
import { Dialog } from '@bb-ui/react-library/dist/components/Dialog';
import { DialogProps } from '@bb-ui/react-library/dist/components/Dialog/Dialog.types';
import { DialogTitle } from '@bb-ui/react-library/dist/components/DialogTitle';
import { TenantData } from 'App.types';
import { useAuthContext } from 'contexts/AuthContext';
import { useSnackbar } from 'hooks/useSnackbar';
import { ReleaseStageMember } from 'pages/ReleaseStageMembers/ReleaseStageMembers.types';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from 'utils/apiErrorMessage';
import { apiUrl } from 'utils/apiUrl';

interface MembershipResponse {
  items?: ReleaseStageMember[];
}

interface LearnInstanceSummary {
  InstallationId?: string;
  TenantId?: string;
}

interface LearnInstancesResponse {
  items?: LearnInstanceSummary[];
  nextPageToken?: string;
}

const MAX_INSTALLATION_IDS = 60;

interface InstallationIdValidation {
  totalIds: number;
  uniqueIds: string[];
  duplicates: string[];
  hasError: boolean;
}

export interface ReleaseStageSiteError {
  installationId: string;
  message: string;
}

export const parseInstallationIds = (value: string): string[] =>
  value.split(/[\s,;]+/).filter(Boolean);

export const validateInstallationIds = (value: string): InstallationIdValidation => {
  const installationIds = parseInstallationIds(value);
  const duplicates = Array.from(
    new Set(installationIds.filter((id, index) => installationIds.indexOf(id) !== index)),
  );

  return {
    totalIds: installationIds.length,
    uniqueIds: Array.from(new Set(installationIds)),
    duplicates,
    hasError: installationIds.length > MAX_INSTALLATION_IDS || duplicates.length > 0,
  };
};

const useStyles = makeStyles((theme) => ({
  input: {
    marginTop: theme.spacing(1),
    minWidth: 0,
    width: '100%',
  },
  errorBanner: {
    marginTop: theme.spacing(2),
  },
  errorList: {
    marginBottom: 0,
    paddingLeft: theme.spacing(3),
  },
  errorSite: {
    fontWeight: 'bold',
  },
}));

export interface AddReleaseStageSitesDialogProps extends DialogProps {
  onClose: () => void;
  onSitesAdded?: (tenantIds: string[]) => void;
  releaseStageId: string;
  releaseStageName: string;
}

export const AddReleaseStageSitesDialog: React.FunctionComponent<
  AddReleaseStageSitesDialogProps
> = ({ id, open, onClose, onSitesAdded, releaseStageId, releaseStageName, ...dialogProps }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { idToken } = useAuthContext();
  const classes = useStyles();
  const [installationIdsInput, setInstallationIdsInput] = React.useState('');
  const [errors, setErrors] = React.useState<ReleaseStageSiteError[]>([]);
  const [submitting, setSubmitting] = React.useState(false);
  const inputValidation = React.useMemo(
    () => validateInstallationIds(installationIdsInput),
    [installationIdsInput],
  );
  const authConfig = React.useMemo(
    () => ({ headers: { Authorization: `Bearer ${idToken}` } }),
    [idToken],
  );

  React.useEffect(() => {
    if (!open) {
      setInstallationIdsInput('');
      setErrors([]);
      setSubmitting(false);
    }
  }, [open]);

  const requestClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  const addSites = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const installationIds = inputValidation.uniqueIds;

    if (installationIds.length === 0) {
      setErrors([
        {
          installationId: t('releaseStageMembers.addSitesDialog.siteIdsLabel'),
          message: t('releaseStageMembers.addSitesDialog.siteIdsRequired'),
        },
      ]);
      return;
    }

    if (inputValidation.hasError) {
      return;
    }

    setSubmitting(true);
    setErrors([]);

    const validationErrors: ReleaseStageSiteError[] = [];
    const instancesByInstallationId = new Map<string, LearnInstanceSummary>();

    try {
      const loadInstances = async (nextPageToken?: string): Promise<void> => {
        const query = nextPageToken ? `?nextPageToken=${encodeURIComponent(nextPageToken)}` : '';
        const response = await axios.get<LearnInstancesResponse>(
          apiUrl('lct', `instances/learnInstances${query}`),
          authConfig,
        );
        response.data?.items?.forEach((instance) => {
          if (instance.InstallationId) {
            instancesByInstallationId.set(instance.InstallationId, instance);
          }
        });
        if (response.data?.nextPageToken) {
          await loadInstances(response.data.nextPageToken);
        }
      };
      await loadInstances();
    } catch (error) {
      const message = getApiErrorMessage(
        error,
        t('releaseStageMembers.addSitesDialog.siteLookupFailed'),
      );
      setErrors(installationIds.map((installationId) => ({ installationId, message })));
      setSubmitting(false);
      return;
    }

    try {
      const results = await Promise.allSettled(
        installationIds.map(async (installationId) => {
          const tenantId = instancesByInstallationId.get(installationId)?.TenantId;
          if (!tenantId) {
            throw new Error(t('releaseStageMembers.addSitesDialog.siteNotFound'));
          }

          let tenant: TenantData;

          try {
            const response = await axios.get<TenantData>(
              apiUrl('tenancy', `tenants/${encodeURIComponent(tenantId)}`),
              authConfig,
            );
            tenant = response.data;
          } catch (error) {
            if ((error as AxiosError)?.response?.status === 404) {
              throw new Error(t('releaseStageMembers.addSitesDialog.tenantNotFound'));
            }
            throw new Error(
              getApiErrorMessage(
                error,
                t('releaseStageMembers.addSitesDialog.tenantValidationFailed'),
              ),
            );
          }

          if (tenant.tenantType !== 'LearnInstance') {
            throw new Error(t('releaseStageMembers.addSitesDialog.notLearnTenant'));
          }

          let memberships: ReleaseStageMember[];
          try {
            const response = await axios.get<MembershipResponse>(
              apiUrl('lct', `releaseStage/tenants/${encodeURIComponent(tenantId)}`),
              authConfig,
            );
            memberships = response.data?.items ?? [];
          } catch (error) {
            throw new Error(
              getApiErrorMessage(
                error,
                t('releaseStageMembers.addSitesDialog.membershipValidationFailed'),
              ),
            );
          }

          if (memberships.some((membership) => membership.ReleaseStageId === releaseStageId)) {
            throw new Error(t('releaseStageMembers.addSitesDialog.alreadyCurrentMember'));
          }

          try {
            await axios.post(
              apiUrl('lct', `releaseStage/tenants/${encodeURIComponent(tenantId)}`),
              {
                releaseStageId,
                tenantName: tenant.displayName || tenant.name || tenantId,
              },
              authConfig,
            );
          } catch (error) {
            throw new Error(
              getApiErrorMessage(error, t('releaseStageMembers.addSitesDialog.addFailed')),
            );
          }

          return { installationId, tenantId };
        }),
      );

      const addedTenantIds: string[] = [];
      const addedInstallationIds: string[] = [];

      results.forEach((result, index) => {
        const installationId = installationIds[index];
        if (result.status === 'fulfilled') {
          addedInstallationIds.push(result.value.installationId);
          addedTenantIds.push(result.value.tenantId);
        } else {
          validationErrors.push({
            installationId,
            message: getApiErrorMessage(
              result.reason,
              t('releaseStageMembers.addSitesDialog.requestFailed'),
            ),
          });
        }
      });

      if (addedTenantIds.length > 0) {
        enqueueSnackbar(
          t('releaseStageMembers.addSitesDialog.addSucceeded', { count: addedTenantIds.length }),
          { variant: 'success' },
        );
        onSitesAdded?.(addedTenantIds);
      }

      setErrors(validationErrors);

      if (validationErrors.length === 0) {
        onClose();
      } else {
        const addedInstallationIdSet = new Set(addedInstallationIds);
        setInstallationIdsInput(
          installationIds
            .filter((installationId) => !addedInstallationIdSet.has(installationId))
            .join('\n'),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      id={id}
      open={open}
      onClose={requestClose}
      aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-instructions`}
      maxWidth="md"
      fullWidth
      {...dialogProps}
    >
      <DialogTitle onClose={requestClose} id={`${id}-title`} closeLabel={t('global.close')}>
        {t('releaseStageMembers.addSitesDialog.title', { releaseStageName })}
      </DialogTitle>
      <form onSubmit={addSites} noValidate data-testid="add-release-stage-sites-form">
        <DialogContent>
          <Typography id={`${id}-instructions`}>
            {t('releaseStageMembers.addSitesDialog.instructions')}
          </Typography>
          <TextField
            autoFocus
            className={classes.input}
            data-testid="add-release-stage-sites-input"
            disabled={submitting}
            fullWidth
            label={t('releaseStageMembers.addSitesDialog.siteIdsLabel')}
            multiline
            onChange={(event) => setInstallationIdsInput(event.target.value)}
            placeholder={t('releaseStageMembers.addSitesDialog.siteIdsPlaceholder')}
            rows={10}
            value={installationIdsInput}
          />
          {inputValidation.hasError && (
            <Banner
              severity="error"
              className={classes.errorBanner}
              data-testid="add-sites-input-errors"
            >
              {inputValidation.totalIds > MAX_INSTALLATION_IDS && (
                <Typography>
                  {t('releaseStageMembers.addSitesDialog.maxExceeded', {
                    count: inputValidation.totalIds,
                    excess: inputValidation.totalIds - MAX_INSTALLATION_IDS,
                  })}
                </Typography>
              )}
              {inputValidation.duplicates.length > 0 && (
                <>
                  <Typography>
                    {t('releaseStageMembers.addSitesDialog.duplicatesFound', {
                      count: inputValidation.duplicates.length,
                    })}
                  </Typography>
                  <ul className={classes.errorList}>
                    {inputValidation.duplicates.map((installationId) => (
                      <li key={installationId}>{installationId}</li>
                    ))}
                  </ul>
                </>
              )}
            </Banner>
          )}
          {errors.length > 0 && (
            <Banner severity="error" className={classes.errorBanner} data-testid="add-sites-errors">
              <Typography>{t('releaseStageMembers.addSitesDialog.errorsTitle')}</Typography>
              <ul className={classes.errorList}>
                {errors.map((error, index) => (
                  <li key={`${error.installationId}-${index}`}>
                    <span className={classes.errorSite}>{error.installationId}:</span>{' '}
                    {error.message}
                  </li>
                ))}
              </ul>
            </Banner>
          )}
        </DialogContent>
        <DialogActions>
          <PrimaryButton
            type="submit"
            disabled={submitting || !installationIdsInput.trim() || inputValidation.hasError}
          >
            {submitting
              ? t('releaseStageMembers.addSitesDialog.adding')
              : t('releaseStageMembers.addSitesDialog.confirm')}
          </PrimaryButton>
          <DefaultButton onClick={requestClose} disabled={submitting}>
            {t('global.cancel')}
          </DefaultButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddReleaseStageSitesDialog;
